import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { GetPaymentOrdersResponse } from "features/stock-ordering/core/domain/payment.model";
import { GetPaymentOrdersParam } from "features/stock-ordering/core/stock-ordering.params";
import {
  GetPaymentOrdersRepository,
  GetPaymentOrdersRepoResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

export enum GetPaymentOrdersState {
  initial,
  inProgress,
  success,
  fail,
}

interface InitialState {
  status: GetPaymentOrdersState;
  message: string;
  data: GetPaymentOrdersResponse | undefined;
}

const initialState: InitialState = {
  status: GetPaymentOrdersState.initial,
  message: "",
  data: undefined,
};

export const getPaymentOrders = createAsyncThunk(
  "getPaymentOrders",
  async (param: GetPaymentOrdersParam | undefined, { rejectWithValue }) => {
    try {
      const response: GetPaymentOrdersRepoResponse =
        await GetPaymentOrdersRepository(param);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  },
);

export const getPaymentOrdersSlice = createSlice({
  name: "getPaymentOrders",
  initialState,
  reducers: {
    resetGetPaymentOrders: (state) => {
      state.status = GetPaymentOrdersState.initial;
      state.data = undefined;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentOrders.pending, (state) => {
        state.status = GetPaymentOrdersState.inProgress;
      })
      .addCase(getPaymentOrders.fulfilled, (state, action) => {
        if (action.payload) {
          const { message, data } = action.payload;
          state.status = GetPaymentOrdersState.success;
          state.message = message;
          state.data = data;
        }
      })
      .addCase(getPaymentOrders.rejected, (state, action) => {
        state.status = GetPaymentOrdersState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

export const selectGetPaymentOrders = (state: RootState) =>
  state.getPaymentOrders;
export const { resetGetPaymentOrders } = getPaymentOrdersSlice.actions;
export default getPaymentOrdersSlice.reducer;
